<template>
  <div v-if="isLoading">
    <div class="poker-loading-container" role="status" aria-live="polite">
      <div class="loading-stars loading-stars--small" aria-hidden="true"></div>
      <div class="loading-stars loading-stars--medium" aria-hidden="true"></div>
      <div class="loading-stars loading-stars--large" aria-hidden="true"></div>
      <div class="poker-cards-loading">
        <img
          ref="animationImage"
          :src="pokerLoadingAnimation"
          alt=""
          class="loading-card"
          width="600"
          height="676"
          decoding="async"
          fetchpriority="high"
          @load="handleAnimationReady"
          @error="handleAnimationReady"
        />
      </div>
      <p class="loading-text" aria-label="loading...">
        loading<span class="loading-dots" aria-hidden="true">
          <span class="loading-dot">.</span>
          <span class="loading-dot">.</span>
          <span class="loading-dot">.</span>
        </span>
      </p>
    </div>
  </div>
</template>

<script setup lang="ts">
import { nextTick, onMounted, ref, watch } from 'vue';
import pokerLoadingAnimation from '@/assets/img/pokerCards/poker-loading.webp';

const props = defineProps<{
  isLoading: boolean;
}>();

const emit = defineEmits<{
  ready: [];
}>();

const animationImage = ref<HTMLImageElement | null>(null);
let hasEmittedReady = false;

// 載入成功或失敗都通知父層，避免 Loading 因單一圖片失敗而卡住。
const handleAnimationReady = () => {
  if (hasEmittedReady) return;

  hasEmittedReady = true;
  emit('ready');
};

// SSR 時圖片可能在 Vue 綁定 @load 前就完成，需主動補查完成狀態。
onMounted(() => {
  if (animationImage.value?.complete) {
    handleAnimationReady();
  }
});

watch(
  () => props.isLoading,
  async (isLoading) => {
    if (!isLoading) {
      hasEmittedReady = false;
      return;
    }

    hasEmittedReady = false;
    await nextTick();

    if (props.isLoading && animationImage.value?.complete) {
      handleAnimationReady();
    }
  },
  { flush: 'post' }
);
</script>

<style lang="scss" scoped>
@use '@/assets/css/main.scss' as *;

@function multiple-box-shadow($count, $color) {
  $value: '#{random(2000)}px #{random(2000)}px #{$color}';

  @for $index from 2 through $count {
    $value: '#{$value}, #{random(2000)}px #{random(2000)}px #{$color}';
  }

  @return unquote($value);
}

$stars-small: multiple-box-shadow(500, rgba(255, 255, 255, 0.8));
$stars-medium: multiple-box-shadow(160, rgba(255, 240, 163, 0.9));
$stars-large: multiple-box-shadow(80, rgba(215, 169, 54, 1));

.poker-loading-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: radial-gradient(ellipse at bottom, #212023 0%, #050505 100%);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 9999;
  backdrop-filter: blur(5px);
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0px;
  overflow: hidden;
  isolation: isolate;
}

.loading-stars {
  position: absolute;
  top: 0;
  left: 0;
  width: 1px;
  height: 1px;
  background: transparent;
  pointer-events: none;
  z-index: 0;

  &::after {
    content: '';
    position: absolute;
    top: 2000px;
    left: 0;
    width: inherit;
    height: inherit;
    background: transparent;
  }
}

.loading-stars--small {
  box-shadow: $stars-small;
  animation: loading-star-scroll 50s linear infinite;

  &::after {
    box-shadow: $stars-small;
  }
}

.loading-stars--medium {
  width: 2px;
  height: 2px;
  box-shadow: $stars-medium;
  animation: loading-star-scroll 100s linear infinite;

  &::after {
    box-shadow: $stars-medium;
  }
}

.loading-stars--large {
  width: 3px;
  height: 3px;
  box-shadow: $stars-large;
  animation: loading-star-scroll 150s linear infinite;

  &::after {
    box-shadow: $stars-large;
  }
}

@media (prefers-reduced-motion: reduce) {
  .loading-stars {
    animation: none;
  }
}

.poker-cards-loading {
  position: relative;
  width: 300px;
  height: 400px;
  display: flex;
  justify-content: center;
  align-items: center;
  opacity: 1;
  z-index: 1;

  @media screen and (max-width: $breakpoint-med) {
    width: 200px;
    height: 280px;
  }

  @media screen and (max-width: $breakpoint-smaller) {
    width: 150px;
    height: 210px;
  }
}

.loading-card {
  position: absolute;
  width: 100%;
  height: auto;
  object-fit: contain;
  filter: drop-shadow(0 0 10px rgba(255, 193, 7, 0.3));
}

@keyframes cardGlow {
  0% {
    filter: drop-shadow(0 0 10px rgba(255, 193, 7, 0.3));
    transform: scale(0.98);
  }
  50% {
    filter: drop-shadow(0 0 30px rgba(255, 193, 7, 0.6));
    transform: scale(1.02);
  }
  100% {
    filter: drop-shadow(0 0 20px rgba(255, 193, 7, 0.3));
    transform: scale(1);
  }
}

@keyframes loading-star-scroll {
  from {
    transform: translateY(0);
  }
  to {
    transform: translateY(-2000px);
  }
}

.loading-text {
  position: relative;
  z-index: 1;
  display: inline-block;
  opacity: 1;
  font-size: 24px;
  font-weight: bold;
  color: transparent;
  background: repeating-linear-gradient(
    110deg,
    #8c641b 0 55px,
    #fff0a3 75px,
    #d7a936 100px,
    #8c641b 130px,
    #8c641b 220px
  );
  background-size: 220px 100%;
  background-position: 0 0;
  background-clip: text;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  text-transform: uppercase;
  letter-spacing: 4px;
  filter: drop-shadow(4px 4px 0 rgba(0, 0, 0, 0.1)) drop-shadow(7px 7px 4px rgba(0, 0, 0, 0.1))
    drop-shadow(0 0 6px rgba(255, 193, 7, 0.2));
  animation: loading-text-shimmer 2.2s linear infinite;
  animation-play-state: running;
  will-change: background-position;

  @media screen and (max-width: $breakpoint-smaller) {
    font-size: 18px;
    letter-spacing: 2px;
  }
}

.loading-dots {
  display: inline-flex;
  gap: 0.1em;
  letter-spacing: 0;
  vertical-align: baseline;
}

.loading-dot {
  position: relative;
  display: inline-block;
  top: 0;
  animation: loading-dot-bounce 1.2s ease-in-out infinite;
  animation-play-state: running;
  will-change: top, opacity;

  &:nth-child(2) {
    animation-delay: 0.14s;
  }

  &:nth-child(3) {
    animation-delay: 0.28s;
  }
}

@keyframes loading-dot-bounce {
  0%,
  60%,
  100% {
    opacity: 0.55;
    top: 0;
  }
  30% {
    opacity: 1;
    top: -0.35em;
  }
}

@keyframes loading-text-shimmer {
  from {
    background-position: 0 0;
  }
  to {
    background-position: 220px 0;
  }
}

@keyframes pulse {
  0%,
  100% {
    opacity: 0.6;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.05);
  }
}
</style>
